import { SPECIFIC_SUGGESTION_REGEX } from 'core/constants/regex';

type MessageSegment = {
  raw: string;
  visibleLength: number;
  isChip: boolean;
};

export type TruncatedMessage = {
  body: string;
  isTruncated: boolean;
};

const toSegments = (messageBody: string): MessageSegment[] => {
  const matches = Array.from(messageBody.matchAll(SPECIFIC_SUGGESTION_REGEX));

  const { segments, endIndex } = matches.reduce<{
    segments: MessageSegment[];
    endIndex: number;
  }>(
    (acc, match) => {
      const matchIndex = match.index ?? acc.endIndex;
      const textBefore = messageBody.substring(acc.endIndex, matchIndex);

      return {
        segments: [
          ...acc.segments,
          ...(textBefore
            ? [
                {
                  raw: textBefore,
                  visibleLength: textBefore.length,
                  isChip: false
                }
              ]
            : []),
          { raw: match[0], visibleLength: match[1].length, isChip: true }
        ],
        endIndex: matchIndex + match[0].length
      };
    },
    { segments: [], endIndex: 0 }
  );

  const textAfter = messageBody.substring(endIndex);

  return textAfter
    ? [
        ...segments,
        { raw: textAfter, visibleLength: textAfter.length, isChip: false }
      ]
    : segments;
};

/**
 * Truncates a message body by how much of it the user can actually see.
 *
 * Suggestion chips are stored in the body as <specific_suggestion> tags, so a
 * plain slice of the raw string both counts invisible markup towards the limit
 * and can cut through a tag, leaving it to render as visible text.
 */
export const truncateMessagePreservingChips = (
  messageBody: string,
  limit: number
): TruncatedMessage => {
  const segments = toSegments(messageBody);
  const visibleLength = segments.reduce(
    (total, segment) => total + segment.visibleLength,
    0
  );

  if (visibleLength <= limit) {
    return { body: messageBody, isTruncated: false };
  }

  const { parts } = segments.reduce<{
    parts: string[];
    used: number;
    isDone: boolean;
  }>(
    (acc, segment) => {
      if (acc.isDone) {
        return acc;
      }

      const remaining = limit - acc.used;

      if (segment.visibleLength <= remaining) {
        return {
          parts: [...acc.parts, segment.raw],
          used: acc.used + segment.visibleLength,
          isDone: false
        };
      }

      // Never cut through a chip - drop it and everything after it instead.
      if (segment.isChip) {
        return { ...acc, isDone: true };
      }

      return {
        parts: [...acc.parts, segment.raw.substring(0, remaining)],
        used: limit,
        isDone: true
      };
    },
    { parts: [], used: 0, isDone: false }
  );

  // A single chip wider than the whole limit would otherwise leave nothing to
  // show, so keep the first segment rather than collapsing to an ellipsis.
  const visibleParts = parts.length
    ? parts
    : segments.slice(0, 1).map((segment) => segment.raw);

  const body = visibleParts.join('');

  return { body, isTruncated: body !== messageBody };
};
